import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { isAtLeast } from '../utils/roles';
import {
  fetchAuditLogs,
  AuditLogListItem,
  AuditLogAction,
  AuditLogsQueryParams,
} from '../api/audit';

// ── Constants ─────────────────────────────────────────────────

const MODEL_NAMES = [
  'Event',
  'Venue',
  'SubCommittee',
  'Task',
  'Member',
  'Vendor',
  'KduSponsor',
  'FundraisingActivity',
  'BookletSubmission',
  'TicketOrder',
  'Workflow',
  'Chapter',
  'BudgetItem',
  'GuestRecord',
  'EventTask',
  'Document',
  'KnowledgeEntry',
  'Registration',
  'Announcement',
  'Meeting',
  'BudgetSubmission',
  'MeetingMinute',
  'TicketTier',
  'WorkflowStep',
  'AgendaItem',
  'MealOption',
  'BanquetTable',
  'Seat',
];

const ACTION_TYPES: AuditLogAction[] = [
  'CREATE',
  'UPDATE',
  'DELETE',
  'BULK_CREATE',
  'BULK_UPDATE',
  'BULK_DELETE',
];

const DEFAULT_PAGE_SIZE = 50;
const MAX_PAGE_SIZE = 200;
const MAX_DATE_RANGE_MONTHS = 12;

// ── Styles ────────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  page: { padding: '2rem', maxWidth: '1200px', margin: '0 auto' },
  title: { color: '#1a6b3c', fontSize: '1.75rem', margin: '0 0 0.5rem 0' },
  subtitle: { color: '#666', fontSize: '0.95rem', marginBottom: '1.5rem' },
  accessDenied: {
    padding: '2rem',
    textAlign: 'center' as const,
    color: '#d32f2f',
  },
  filtersRow: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '12px',
    marginBottom: '16px',
    alignItems: 'flex-end',
  },
  filterGroup: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '4px',
  },
  filterLabel: {
    fontSize: '11px',
    fontWeight: 600,
    color: '#666',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.3px',
  },
  select: {
    padding: '8px 12px',
    border: '1px solid #ccc',
    borderRadius: '6px',
    fontSize: '13px',
    backgroundColor: '#fff',
    minWidth: '140px',
  },
  input: {
    padding: '8px 12px',
    border: '1px solid #ccc',
    borderRadius: '6px',
    fontSize: '13px',
    boxSizing: 'border-box' as const,
    minWidth: '180px',
  },
  dateInput: {
    padding: '8px 12px',
    border: '1px solid #ccc',
    borderRadius: '6px',
    fontSize: '13px',
    boxSizing: 'border-box' as const,
    minWidth: '140px',
  },
  clearBtn: {
    padding: '8px 14px',
    backgroundColor: '#f5f5f5',
    color: '#333',
    border: '1px solid #e0e0e0',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    backgroundColor: '#fff',
    borderRadius: '8px',
    overflow: 'hidden',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  th: {
    textAlign: 'left' as const,
    padding: '12px 16px',
    fontSize: '12px',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    color: '#666',
    borderBottom: '2px solid #1a6b3c',
    fontWeight: 600,
  },
  td: {
    padding: '12px 16px',
    fontSize: '14px',
    borderBottom: '1px solid #eee',
    color: '#333',
    verticalAlign: 'middle' as const,
  },
  row: {
    cursor: 'pointer',
    transition: 'background-color 0.1s',
  },
  rowSelected: {
    cursor: 'pointer',
    backgroundColor: '#e8f5e9',
  },
  actionBadge: {
    display: 'inline-block',
    padding: '3px 10px',
    borderRadius: '12px',
    fontSize: '11px',
    fontWeight: 600,
  },
  pagination: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '16px',
    padding: '12px 0',
  },
  paginationInfo: {
    fontSize: '13px',
    color: '#666',
  },
  paginationControls: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  pageBtn: {
    padding: '6px 12px',
    backgroundColor: '#fff',
    color: '#1a6b3c',
    border: '1px solid #1a6b3c',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  pageBtnDisabled: {
    padding: '6px 12px',
    backgroundColor: '#f5f5f5',
    color: '#999',
    border: '1px solid #e0e0e0',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'not-allowed',
  },
  pageSizeSelect: {
    padding: '6px 10px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '12px',
  },
  loading: {
    textAlign: 'center' as const,
    padding: '3rem',
    color: '#666',
    fontSize: '14px',
  },
  emptyState: {
    textAlign: 'center' as const,
    padding: '3rem',
    color: '#666',
    fontSize: '14px',
  },
  error: {
    color: '#d32f2f',
    backgroundColor: '#fdecea',
    padding: '10px 16px',
    borderRadius: '8px',
    marginBottom: '16px',
    fontSize: '13px',
    border: '1px solid #f5c6cb',
  },
  dateRangeError: {
    fontSize: '11px',
    color: '#d32f2f',
    marginTop: '2px',
  },
};

// ── Helpers ───────────────────────────────────────────────────

function getActionBadgeColor(action: AuditLogAction): React.CSSProperties {
  switch (action) {
    case 'CREATE':
    case 'BULK_CREATE':
      return { backgroundColor: '#e8f5e9', color: '#1a6b3c' };
    case 'UPDATE':
    case 'BULK_UPDATE':
      return { backgroundColor: '#e3f2fd', color: '#1565c0' };
    case 'DELETE':
    case 'BULK_DELETE':
      return { backgroundColor: '#fdecea', color: '#c62828' };
    default:
      return { backgroundColor: '#f5f5f5', color: '#666' };
  }
}

function formatTimestamp(ts: string): string {
  const date = new Date(ts);
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function isDateRangeValid(startDate: string, endDate: string): boolean {
  if (!startDate || !endDate) return true;
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (end < start) return false;
  const diffMs = end.getTime() - start.getTime();
  const diffMonths = diffMs / (1000 * 60 * 60 * 24 * 30.44);
  return diffMonths <= MAX_DATE_RANGE_MONTHS;
}

// ── Component ─────────────────────────────────────────────────

export function AuditHistoryPage() {
  const { user } = useAuth();

  // Filter state
  const [modelName, setModelName] = useState('');
  const [actionType, setActionType] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [search, setSearch] = useState('');

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // Data state
  const [entries, setEntries] = useState<AuditLogListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Selection state
  const [selectedEntryId, setSelectedEntryId] = useState<string | null>(null);

  // Date range validation
  const dateRangeValid = isDateRangeValid(startDate, endDate);

  const loadData = useCallback(async () => {
    if (!dateRangeValid) return;

    setLoading(true);
    setError('');

    const params: AuditLogsQueryParams = {
      page,
      pageSize,
    };

    if (modelName) params.modelName = modelName;
    if (actionType) params.action = actionType as AuditLogAction;
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    if (search.trim()) params.search = search.trim();

    try {
      const response = await fetchAuditLogs(params);
      setEntries(response.data);
      setTotal(response.total);
      setTotalPages(response.totalPages);
    } catch (err) {
      setError('Failed to load audit logs. Please try again.');
      setEntries([]);
      setTotal(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [modelName, actionType, startDate, endDate, search, page, pageSize, dateRangeValid]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [modelName, actionType, startDate, endDate, search, pageSize]);

  const handleClearFilters = () => {
    setModelName('');
    setActionType('');
    setStartDate('');
    setEndDate('');
    setSearch('');
    setPage(1);
  };

  const handlePageSizeChange = (newSize: number) => {
    const clamped = Math.min(Math.max(newSize, 1), MAX_PAGE_SIZE);
    setPageSize(clamped);
  };

  // Access check
  if (!user || !isAtLeast(user.role, 'Admin')) {
    return (
      <div style={styles.accessDenied}>
        <h2>Access Denied</h2>
        <p>You do not have permission to view audit history.</p>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <h1 style={styles.title}>Audit History</h1>
      <p style={styles.subtitle}>
        View and search the complete audit trail of data changes across the system.
      </p>

      {/* Filters */}
      <div style={styles.filtersRow}>
        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>Model</label>
          <select
            style={styles.select}
            value={modelName}
            onChange={(e) => setModelName(e.target.value)}
            aria-label="Filter by model name"
          >
            <option value="">All Models</option>
            {MODEL_NAMES.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </div>

        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>Action</label>
          <select
            style={styles.select}
            value={actionType}
            onChange={(e) => setActionType(e.target.value)}
            aria-label="Filter by action type"
          >
            <option value="">All Actions</option>
            {ACTION_TYPES.map((action) => (
              <option key={action} value={action}>
                {action}
              </option>
            ))}
          </select>
        </div>

        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>Search</label>
          <input
            style={styles.input}
            type="text"
            placeholder="Search record IDs or actor names..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search audit logs by record ID or actor name"
          />
        </div>

        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>Start Date</label>
          <input
            style={styles.dateInput}
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            aria-label="Filter start date"
          />
        </div>

        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>End Date</label>
          <input
            style={styles.dateInput}
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            aria-label="Filter end date"
          />
        </div>

        <button
          style={styles.clearBtn}
          onClick={handleClearFilters}
          aria-label="Clear all filters"
        >
          Clear Filters
        </button>
      </div>

      {/* Date range validation error */}
      {!dateRangeValid && (
        <p style={styles.dateRangeError}>
          Date range must not exceed 12 months and end date must be after start date.
        </p>
      )}

      {/* Error */}
      {error && <div style={styles.error}>{error}</div>}

      {/* Loading */}
      {loading && <div style={styles.loading}>Loading audit logs...</div>}

      {/* Table */}
      {!loading && entries.length > 0 && (
        <>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Timestamp</th>
                <th style={styles.th}>Actor</th>
                <th style={styles.th}>Model</th>
                <th style={styles.th}>Record ID</th>
                <th style={styles.th}>Action</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr
                  key={entry.id}
                  style={selectedEntryId === entry.id ? styles.rowSelected : styles.row}
                  onClick={() => setSelectedEntryId(entry.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      setSelectedEntryId(entry.id);
                    }
                  }}
                >
                  <td style={styles.td}>{formatTimestamp(entry.timestamp)}</td>
                  <td style={styles.td}>
                    {entry.actorName || 'System (pre-audit)'}
                  </td>
                  <td style={styles.td}>{entry.modelName}</td>
                  <td style={styles.td}>
                    <code style={{ fontSize: '12px', backgroundColor: '#f5f5f5', padding: '2px 6px', borderRadius: '3px' }}>
                      {entry.recordId}
                    </code>
                  </td>
                  <td style={styles.td}>
                    <span style={{ ...styles.actionBadge, ...getActionBadgeColor(entry.action) }}>
                      {entry.action}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          <div style={styles.pagination}>
            <span style={styles.paginationInfo}>
              Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total} entries
            </span>

            <div style={styles.paginationControls}>
              <label style={{ fontSize: '12px', color: '#666' }}>
                Per page:
                <select
                  style={{ ...styles.pageSizeSelect, marginLeft: '4px' }}
                  value={pageSize}
                  onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                  aria-label="Page size"
                >
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={200}>200</option>
                </select>
              </label>

              <button
                style={page <= 1 ? styles.pageBtnDisabled : styles.pageBtn}
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                aria-label="Previous page"
              >
                ← Prev
              </button>

              <span style={{ fontSize: '12px', color: '#666' }}>
                Page {page} of {totalPages}
              </span>

              <button
                style={page >= totalPages ? styles.pageBtnDisabled : styles.pageBtn}
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                aria-label="Next page"
              >
                Next →
              </button>
            </div>
          </div>
        </>
      )}

      {/* Empty state */}
      {!loading && !error && entries.length === 0 && (
        <div style={styles.emptyState}>
          <p>No audit log entries found.</p>
          <p style={{ fontSize: '12px', color: '#999' }}>
            Try adjusting your filters or search criteria.
          </p>
        </div>
      )}
    </div>
  );
}
